import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { api } from "../../api/api";
import {
  getItemizerCodesForToken,
  hasTokenItemizerTabs,
} from "../../utils/itemizerRules";

export const getCompanies = createAsyncThunk(
  "companies/getCompanies",
  async (_, thunkApi) => {
    const { token } = await thunkApi.getState().auth;
    const { data } = await api.getCompanies({ x_token: token });
    return data;
  }
);

export const syncCompany = createAsyncThunk(
  "companies/syncCompany",
  async (_, thunkApi) => {
    const { token } = await thunkApi.getState().auth;
    const { currentId } = await thunkApi.getState().companies;
    const { data } = await api.syncCompany({
      x_token: token,
      company_id: currentId,
    });
    return data;
  }
);

// tables
export const getFloors = createAsyncThunk(
  "companies/getFloors",
  async (_, thunkApi) => {
    const { token } = await thunkApi.getState().auth;
    const { currentId } = await thunkApi.getState().companies;
    const { data } = await api.getFloors({
      x_token: token,
      company_id: currentId,
    });
    return data;
  }
);

export const getOutlets = createAsyncThunk(
  "companies/getOutlets",
  async (_, thunkApi) => {
    const { token } = await thunkApi.getState().auth;
    const { currentId } = await thunkApi.getState().companies;
    const { data } = await api.getOutlets({
      type: "outlets",
      x_token: token,
      company_id: currentId,
    });
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    return data && typeof data === "object" ? [data] : [];
  }
);

export const updateFloor = createAsyncThunk(
  "companies/updateFloor",
  async (updateData, thunkApi) => {
    const { token } = await thunkApi.getState().auth;
    const { data } = await api.updateFloor({
      x_token: token,
      ...updateData,
    });
    return data;
  }
);

export const getItemizers = createAsyncThunk(
  "companies/getItemizers",
  async (itemizerData = {}, thunkApi) => {
    const { token } = await thunkApi.getState().auth;
    const { currentId } = await thunkApi.getState().companies;
    const shouldSendItemizerName = hasTokenItemizerTabs({ token });
    const { data } = await api.getItemizers({
      x_token: token,
      company_id: currentId,
      ...(shouldSendItemizerName && itemizerData.Itemizer
        ? { Itemizer: itemizerData.Itemizer }
        : {}),
    });
    return data;
  }
);
export const getCategoryItemizers = createAsyncThunk(
  "companies/getCategoryItemizers",
  async (_, thunkApi) => {
    const { token } = await thunkApi.getState().auth;
    const { currentId } = await thunkApi.getState().companies;
    const itemizerCodes = getItemizerCodesForToken({ token });

    if (!itemizerCodes.length) {
      const { data } = await api.getItemizers({
        x_token: token,
        company_id: currentId,
      });
      return { byName: {}, slg: data, anahrv: data };
    }

    const responses = await Promise.all(
      itemizerCodes.map((code) =>
        api.getItemizers({
          x_token: token,
          company_id: currentId,
          Itemizer: code,
        })
      )
    );

    const byName = itemizerCodes.reduce(
      (result, code, index) => ({
        ...result,
        [code]: responses[index].data,
      }),
      {}
    );

    return {
      byName,
      slg: byName[itemizerCodes[0]] || [],
      anahrv: byName[itemizerCodes[1]] || [],
    };
  }
);

export const updateItemizer = createAsyncThunk(
  "companies/updateItemizer",
  async (updateData, thunkApi) => {
    const { token } = await thunkApi.getState().auth;
    const { data } = await api.updateItemizer({
      x_token: token,
      ...updateData,
    });
    return data;
  }
);

export const getCategories = createAsyncThunk(
  "companies/getCategories",
  async (_, thunkApi) => {
    const { token } = await thunkApi.getState().auth;
    const { currentId } = await thunkApi.getState().companies;
    const { data } = await api.getCategories({
      x_token: token,
      company_id: currentId,
    });
    return data;
  }
);

export const updateCategory = createAsyncThunk(
  "companies/updateCategory",
  async (updateData, thunkApi) => {
    const { token } = await thunkApi.getState().auth;
    const { data } = await api.updateCategory({
      x_token: token,
      ...updateData,
    });
    return data;
  }
);

export const getPayments = createAsyncThunk(
  "companies/getPayments",
  async (paymentData = {}, thunkApi) => {
    const { token } = await thunkApi.getState().auth;
    const { currentId } = await thunkApi.getState().companies;
    const requestData =
      paymentData && paymentData.constructor === Object ? paymentData : {};
    const { data } = await api.getPayments({
      x_token: token,
      company_id: currentId,
      ...requestData,
    });
    console.log("getPayments 1", currentId)

    if (Array.isArray(data)) return data;

    const paymentRows = data?.payments || data?.data || data?.rows || data?.result;
    const xintegra = data?.xintegra || data?.integra_info || data?.integraInfo;

    if (Array.isArray(paymentRows)) {
      return paymentRows.map((payment) =>
        payment && typeof payment === "object"
          ? { ...(xintegra ? { xintegra } : {}), ...payment }
          : payment
      );
    }

    return data;
  }
);

export const getCardTypes = createAsyncThunk(
  "companies/getCardTypes",
  async (_, thunkApi) => {
    const { token } = await thunkApi.getState().auth;
    const { currentId } = await thunkApi.getState().companies;
    const { data } = await api.getCardTypes({
      x_token: token,
      company_id: currentId,
    });

    return data;
  }
);


export const getCardTypes2 = createAsyncThunk(
  "companies/getCardTypes2",
  async (_, thunkApi) => {
    const { token } = await thunkApi.getState().auth;
    const { currentId } = await thunkApi.getState().companies;
    const { data } = await api.getCardTypes2({
      x_token: token,
      company_id: currentId,
      operation_snt_id : "SNT_HOUSE_ACCOUNTS",
    });

    return data;
  }
);


export const getCardTypes3 = createAsyncThunk(
  "companies/getCardTypes3",
  async (_, thunkApi) => {
    const { token } = await thunkApi.getState().auth;
    const { currentId } = await thunkApi.getState().companies;
    const { data } = await api.getCardTypes3({
      x_token: token,
      company_id: currentId,
      operation_snt_id : "SNT_CHARGE_CODES",
    });

    return data;
  }
);

export const getHouseAccounts = createAsyncThunk(
  "companies/getHouseAccounts",
  async (_, thunkApi) => {
    const { token } = await thunkApi.getState().auth;
    const { currentId } = await thunkApi.getState().companies;
    const { data } = await api.getHouseAccount({
      x_token: token,
      company_id: currentId,
    });

    return data;
  }
);

export const getHouseAccounts2 = createAsyncThunk(
  "companies/getHouseAccounts2",
  async (_, thunkApi) => {
    const { token } = await thunkApi.getState().auth;
    const { currentId } = await thunkApi.getState().companies;
    const { data } = await api.getHouseAccount2({
      x_token: token,
      company_id: currentId,
    });

    return data;
  }
);

export const getTaxes = createAsyncThunk(
  "companies/getTaxes",
  async (_, thunkApi) => {
    const { token } = await thunkApi.getState().auth;
    const { currentId } = await thunkApi.getState().companies;
    const { data } = await api.getTaxes({
      x_token: token,
      company_id: currentId,
    });
    console.log("getTaxes 1", currentId)
    return data;
  }
);

export const updatePayment = createAsyncThunk(
  "companies/updatePayment",
  async (updateData, thunkApi) => {
    const { token } = await thunkApi.getState().auth;
    const { data } = await api.updatePayment({
      x_token: token,
      ...updateData,
    });
    return data;
  }
);

export const updateTax = createAsyncThunk(
  "companies/updateTax",
  async (updateData, thunkApi) => {
    const { token } = await thunkApi.getState().auth;
    const { data } = await api.updateTax({
      x_token: token,
      ...updateData,
    });
    return data;
  }
);
export const getReports = createAsyncThunk(
  "companies/getReports",
  async (dateData, thunkApi) => {
    const { token } = await thunkApi.getState().auth;
    const { currentId } = await thunkApi.getState().companies;
    const { data } = await api.getReports({
      x_token: token,
      company_id: currentId,
      ...dateData,
    });
    //console.log(11)
    return data;
  }
);

export const getLogs = createAsyncThunk(
  "companies/getLogs",
  async (dateData, thunkApi) => {
    const { token } = await thunkApi.getState().auth;
    const { currentId } = await thunkApi.getState().companies;
    const { data } = await api.getLogs({
      x_token: token,
      company_id: currentId,
      ...dateData,
    });

    return data;
  }
);

const initialState = {
  companiesList: [],
  currentId: "",
  loading: false,
  tableLoading: false,
  cardTypesLoading: false,
  houseAccountsLoading: false,
  error: "",
  floors: [],
  outlets: [],
  outletsLoading: false,
  itemizers: [],
  categoryItemizersSlg: [],
  categoryItemizersAnaHrv: [],
  categoryItemizersByName: {},
  categoryItemizersLoading: false,
  categories: [],
  payments: [],
  cardTypes: [],
  houseAccounts: [],
  taxes: [],
  logs: [],
  reports: [],
  houseAccountsSNT: [],   // rezultat getCardTypes2
  chargeCodesSNT: [],     // rezultat getCardTypes3
  houseSNTLoading: false,
  pmsSNTLoading: false,
  sntError: "",
  cardTypes2: [],
  cardTypes3: [],
  cardTypes2Loading: false,
  cardTypes3Loading: false,
};
const pendingDataReducer = (dataName) => (state) => {
  state.tableLoading = true;
  state[dataName] = [];
};
const getDataReducer = (dataName) => (state, action) => {
  state.tableLoading = false;
  state[dataName] = action.payload;
};

const startGettingDataReducer = (dataName) => (state) => {
  state[`${dataName}Loading`] = true;
  state[dataName] = [];
};

const finishGettingDataReducer = (dataName) => (state, action) => {
  state[`${dataName}Loading`] = false;
  state[dataName] = action.payload;
};


const companiesSlice = createSlice({
  name: "companies",
  initialState,
  reducers: {
    setCompanies(state, action) {
      state.companiesList = action.payload;
    },
    setCurrentId(state, action) {
      state.currentId = action.payload;
    },
    setFloors(state, action) {
      state.floors = action.payload;
    },
    updateFloorState(state, action) {
      const newItem = action.payload;
      const oldItem = state.floors.find((i) => i.floor_id === newItem.floor_id);

      oldItem.pms_id = newItem.pms_id;
      oldItem.pms_id_deferred = newItem.pms_id_deferred;
    },
    setItemizers(state, action) {
      state.itemizers = action.payload;
    },
    updateItemizerState(state, action) {
      const newItem = action.payload;
      const oldItem = state.itemizers.find((i) => i.id === newItem.id);

      oldItem.name = newItem.name;
    },
    setCategories(state, action) {
      state.categories = action.payload;
    },
    updateCategoryState(state, action) {
      const newItem = action.payload;
      const oldItem = state.categories.find(
        (i) => i.category_id === newItem.category_id
      );

      oldItem.pms_id_deferred = newItem.pms_id_deferred;
      [
        "pms_name",
        "pms_name1",
        "pms_name2",
        "pms_name3",
        "pms_name4",
        "pms_name5",

      ].forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(newItem, key)) {
          oldItem[key] = newItem[key];
        }
      });
      ["pms_id", "pms_id1", "pms_id2", "pms_id3", "pms_id4", "pms_id5"].forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(newItem, key)) {
          oldItem[key] = newItem[key];
        }
      });
      oldItem.gst_tax_id = newItem.gst_tax_id;
      oldItem.pst_tax_id = newItem.pst_tax_id;
      oldItem.gst_id = newItem.gst_id;
      oldItem.gst_name = newItem.gst_name;
      oldItem.pst_id = newItem.pst_id;
      oldItem.pst_name = newItem.pst_name;
    },
    setPayments(state, action) {
      state.payments = action.payload;
    },
    updatePaymentState(state, action) {
      const newItem = action.payload;
      const isCrossPostingPayments = state.payments.some(
        (i) => Array.isArray(i?.payments)
      );
      const oldItem = isCrossPostingPayments
        ? state.payments
            .find(
              (i) =>
                String(i.hotel_id || "") ===
                String(newItem.parent_hotel_id || newItem.hotel_id || "")
            )
            ?.payments?.find(
              (i) =>
                i.payment_id === newItem.payment_id &&
                String(i.hotel_id || "") === String(newItem.hotel_id || "")
            )
        : state.payments.find((i) => i.payment_id === newItem.payment_id);
      if (!oldItem) return;

      [
        "pms_id",
        "pms_id1",
        "pms_id2",
        "pms_id3",
        "pms_id4",
        "pms_id5",
        "pms_id_deferred",
        "reservation_id",
        "reservation_id1",
        "reservation_id2",
        "reservation_id3",
        "reservation_id4",
        "reservation_id5",
        "reservation_id_deferred",
        "pms_account_id",
        "pms_account_id1",
        "pms_account_id2",
        "pms_account_id3",
        "pms_account_id4",
        "pms_account_id5",
        "start_date",
      ].forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(newItem, key)) {
          oldItem[key] = newItem[key];
        }
      });
    },

    setTaxes(state, action) {
      state.taxes = action.payload;
    },
    updateTaxState(state, action) {
      const newItem = action.payload;
      const oldItem = state.taxes.find(
        (i) => i.tax_id === newItem.tax_id
      );

      oldItem.pms_id = newItem.pms_id;
      oldItem.pms_id_deferred = newItem.pms_id_deferred;
      oldItem.pms_name = newItem.pms_name;
    },
    setLogs(state, action) {
      state.logs = action.payload;
    },
    setReports(state, action) {
      state.reports = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCompanies.pending, (state) => {
        state.error = "";
        state.loading = true;
      })
      .addCase(getCompanies.fulfilled, (state, action) => {
        state.loading = false;
        state.companiesList = action.payload;
      })
      .addCase(getCompanies.rejected, (state) => {
        state.loading = false;
        state.error = "Failed to get companies";
      })
      .addCase(getFloors.pending, pendingDataReducer("floors"))
      .addCase(getOutlets.pending, startGettingDataReducer("outlets"))
      .addCase(getItemizers.pending, pendingDataReducer("itemizers"))
      .addCase(getCategoryItemizers.pending, (state) => {
        state.categoryItemizersLoading = true;
        state.categoryItemizersSlg = [];
        state.categoryItemizersAnaHrv = [];
        state.categoryItemizersByName = {};
      })
      .addCase(getCategories.pending, pendingDataReducer("categories"))
      .addCase(getPayments.pending, pendingDataReducer("payments"))
      .addCase(getCardTypes.pending, startGettingDataReducer("cardTypes"))
      .addCase(getHouseAccounts.pending, startGettingDataReducer("houseAccounts"))
      .addCase(getHouseAccounts2.pending, startGettingDataReducer("houseAccounts"))
      .addCase(getTaxes.pending, pendingDataReducer("taxes"))
      .addCase(getLogs.pending, pendingDataReducer("logs"))
      .addCase(getReports.pending, pendingDataReducer("reports"))
      .addCase(getFloors.fulfilled, getDataReducer("floors"))
      .addCase(getOutlets.fulfilled, finishGettingDataReducer("outlets"))
      .addCase(getOutlets.rejected, (state) => {
        state.outletsLoading = false;
        state.outlets = [];
      })
      .addCase(getItemizers.fulfilled, getDataReducer("itemizers"))
      .addCase(getCategoryItemizers.fulfilled, (state, action) => {
        state.categoryItemizersLoading = false;
        state.categoryItemizersSlg = action.payload.slg || [];
        state.categoryItemizersAnaHrv = action.payload.anahrv || [];
        state.categoryItemizersByName = action.payload.byName || {};
      })
      .addCase(getCategoryItemizers.rejected, (state) => {
        state.categoryItemizersLoading = false;
        state.categoryItemizersSlg = [];
        state.categoryItemizersAnaHrv = [];
        state.categoryItemizersByName = {};
      })
      .addCase(getCategories.fulfilled, getDataReducer("categories"))
      .addCase(getPayments.fulfilled, getDataReducer("payments"))
      .addCase(getCardTypes.fulfilled, finishGettingDataReducer("cardTypes"))
      .addCase(getHouseAccounts.fulfilled, finishGettingDataReducer("houseAccounts"))
      .addCase(getHouseAccounts2.fulfilled, finishGettingDataReducer("houseAccounts"))
      .addCase(getTaxes.fulfilled, getDataReducer("taxes"))
      .addCase(getLogs.fulfilled, getDataReducer("logs"))
      .addCase(getReports.fulfilled, getDataReducer("reports"))

      .addCase(getCardTypes2.pending,  startGettingDataReducer("cardTypes2"))
      .addCase(getCardTypes2.fulfilled, finishGettingDataReducer("cardTypes2"))
      .addCase(getCardTypes3.pending,  startGettingDataReducer("cardTypes3"))
      .addCase(getCardTypes3.fulfilled, finishGettingDataReducer("cardTypes3"))


  },
});

export const {
  setCurrentId,
  updateFloorState,
  updateItemizerState,
  updateCategoryState,
  updatePaymentState,
  updateTaxState,
} = companiesSlice.actions;

export const companiesSelector = (state) => state.companies;
export const currentSelector = (state) => {
  const list = state.companies.companiesList;
  const currentId = state.companies.currentId;

  return {
    currentCompany: list.find((i) => i.company_id === currentId) ?? "",
    currentId,
  };
};

export default companiesSlice.reducer;
